<template>
    <form id="sbLoungeForm" class="form" :class="{ 'not-hydrated': !isHydrated }" @submit.prevent="submitForm()">
        <div class="airport-preloaded">
            <div class="icon">
                <img src="~/assets/images/icon-lounge.webp" alt="airport-lounge-icon" loading="eager" />
            </div>
            <div v-if="!tabs.Title" class="content">
                <span>{{ formData.destination?.label }}</span>
                Airport Lounge
            </div>
            <div v-else class="content">{{ tabs.Title }}</div>
        </div>
        <div class="input-group-container flex !p-0">
            <SearchboxComponentsSimpleDate
                name="departureDate"
                :min-date="new Date()"
                label="Departure Date"
                :allow-single-range="true"
                :value="new Date(formData.dates?.start?.date)"
                :validation="v$.dates?.start?.date"
                class="flex-1"
                @update:value="(value: any) => updateDates(value)"
            />
            <SearchboxComponentsSimpleSelect
                name="entryTime"
                title="Lounge Entry Time"
                :options="entryTimeList"
                :allow-single-range="true"
                :value="formData.dates?.start?.timeRange.value ?? '12:00 - 13:00'"
                :validation="v$.dates?.start?.timeRange"
                append-icon="timer-2-line"
                class="flex-1"
                @update:value="(value: any) => updateTime(value)"
            />
        </div>
        <div class="input-group-container flex !p-0">
            <SearchboxComponentsSimpleSelect
                name="adult"
                title="Adults (12+)"
                label="Adults (12+)"
                :options="[
                    { label: '1', value: '1', disabled: false },
                    { label: '2', value: '2', disabled: false },
                    { label: '3', value: '3', disabled: false },
                    { label: '4', value: '4', disabled: false },
                    { label: '5', value: '5', disabled: false },
                    { label: '6', value: '6', disabled: false },
                ]"
                :value="formData.guests?.guests?.adult.value ? formData.guests.guests?.adult.value : 1"
                class="flex-1"
                @update:value="(value: any) => updateGuests(value, 'adult')"
            />
            <SearchboxComponentsSimpleSelect
                name="children"
                title="Children (2-12)"
                label="Children (2-12)"
                :options="[
                    { label: '0', value: '0', disabled: false },
                    { label: '1', value: '1', disabled: false },
                    { label: '2', value: '2', disabled: false },
                    { label: '3', value: '3', disabled: false },
                    { label: '4', value: '4', disabled: false },
                    { label: '5', value: '5', disabled: false },
                    { label: '6', value: '6', disabled: false },
                ]"
                :value="formData.guests?.guests?.children.value ? formData.guests.guests?.children.value : 0"
                class="flex-1"
                @update:value="(value: any) => updateGuests(value, 'children')"
            />
            <SearchboxComponentsSimpleSelect
                name="infants"
                title="Infants (0-1)"
                label="Infants (0-1)"
                :options="[
                    { label: '0', value: '0', disabled: false },
                    { label: '1', value: '1', disabled: false },
                    { label: '2', value: '2', disabled: false },
                ]"
                :value="formData.guests?.guests?.infants.value ? formData.guests.guests?.infants.value : 0"
                class="flex-1"
                @update:value="(value: any) => updateGuests(value, 'infants')"
            />
        </div>
        <div class="input-group-container flex">
            <div class="submit flex-1">
                <button type="submit" :disabled="!isHydrated">
                    <template v-if="!loading">Search <span class="fullname">Packages</span></template>
                    <NuxtIcon v-show="loading" name="ri:refresh-line" class="animate-spin loading-icon" />
                </button>
            </div>
        </div>
    </form>
</template>
<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, helpers } from '@vuelidate/validators';
import { useSearchStore } from '@nuxt-monorepo/base/app/store/search';
import { redirectToLoungePropel } from '~/mixins/redirectToPropel';

import type { ISearchboxTab } from '@nuxt-monorepo/ui/types';
import type { IAirportLounge } from '~/../types/search';

const { loading, airportList, defaultAirport } = defineProps<{
    tabs: ISearchboxTab;
    airportList: [any];
    defaultAirport: string;
    loading: boolean;
}>();
const emit = defineEmits<{
    (e: 'submitSearch', query: IAirportLounge): void;
}>();

// Constant variable for access to search store
const searchStore = useSearchStore();
const isHydrated = ref(false);

const updateDestination = (val: any) => {
    formData.value.destination = val;
    searchStore.update({ destination: val });
};

const updateDates = (val: any) => {
    formData.value.dates.start.date = val;
    searchStore.update({ dates: formData.value.dates });
};

const updateTime = (val: any) => {
    formData.value.dates.start.timeRange = val;
    searchStore.update({ dates: formData.value.dates });
};

const updateGuests = (val: any, type: string) => {
    formData.value.guests.guests[type] = val;
    searchStore.update({ guests: formData.value.guests });
};

const entryTimeList = [
    { label: '00:00 - 01:00', value: '00:00 - 01:00' },
    { label: '01:00 - 02:00', value: '01:00 - 02:00' },
    { label: '02:00 - 03:00', value: '02:00 - 03:00' },
    { label: '03:00 - 04:00', value: '03:00 - 04:00' },
    { label: '04:00 - 05:00', value: '04:00 - 05:00' },
    { label: '05:00 - 06:00', value: '05:00 - 06:00' },
    { label: '06:00 - 07:00', value: '06:00 - 07:00' },
    { label: '07:00 - 08:00', value: '07:00 - 08:00' },
    { label: '08:00 - 09:00', value: '08:00 - 09:00' },
    { label: '09:00 - 10:00', value: '09:00 - 10:00' },
    { label: '10:00 - 11:00', value: '10:00 - 11:00' },
    { label: '11:00 - 12:00', value: '11:00 - 12:00' },
    { label: '12:00 - 13:00', value: '12:00 - 13:00' },
    { label: '13:00 - 14:00', value: '13:00 - 14:00' },
    { label: '14:00 - 15:00', value: '14:00 - 15:00' },
    { label: '15:00 - 16:00', value: '15:00 - 16:00' },
    { label: '16:00 - 17:00', value: '16:00 - 17:00' },
    { label: '17:00 - 18:00', value: '17:00 - 18:00' },
    { label: '18:00 - 19:00', value: '18:00 - 19:00' },
    { label: '19:00 - 20:00', value: '19:00 - 20:00' },
    { label: '20:00 - 21:00', value: '20:00 - 21:00' },
    { label: '21:00 - 22:00', value: '21:00 - 22:00' },
    { label: '22:00 - 23:00', value: '22:00 - 23:00' },
    { label: '23:00 - 00:00', value: '23:00 - 00:00' },
];

const formData: any = ref({
    destination: airportList.find((airport) => airport.code === defaultAirport),
    // @ts-expect-error - date is not in type
    dates: searchStore.dates?.start?.date ? searchStore.dates : setDefaultDates(),
    // @ts-expect-error - adult is not in type
    guests: !searchStore.guests.guests?.adult
        ? {
              guests: {
                  adult: { label: '1', value: '1', disabled: false },
                  children: { label: '0', value: '0', disabled: false },
                  infants: { label: '0', value: '0', disabled: false },
              },
          }
        : searchStore.guests,
    promo: '',
});

function setDefaultDates() {
    const defaultStartDate = {
        date: getDateDaysAhead(new Date(), 1),
        time: '12:00',
        timeRange: { value: '12:00 - 13:00', label: '12:00 - 13:00' },
    };

    const defaultEndDate = { date: getDateDaysAhead(new Date(), 8), time: '12:00' };

    const defaultDates = {
        start: defaultStartDate,
        end: defaultEndDate,
    };

    searchStore.update({ dates: defaultDates });
    return defaultDates;
}

onMounted(() => {
    isHydrated.value = true;
});

const rules = computed(() => {
    return {
        destination: {
            code: {
                required: helpers.withMessage('Please enter destination', required),
                minLength: minLength(3),
            },
        },
        dates: {
            start: {
                date: {
                    required: helpers.withMessage('Select Date', required),
                },
                timeRange: {
                    required: helpers.withMessage('Select Time Range', required),
                },
            },
        },
    };
});

const v$: any = useVuelidate(rules, formData);

const submitForm = () => {
    v$.value.$validate();

    if (!v$.value.$error) {
        searchStore.update({
            destination: formData.value.destination,
            dates: formData.value.dates,
            guests: formData.value.guests,
        });

        redirectToLoungePropel();

        // COMMENTED OUT: Legacy search
        // const query: IAirportLounge = {
        //     category: 'airportlounge',
        //     airportA: Number(formData.value.destination.value),
        //     airportA_code: formData.value.destination.code,
        //     dateA: formatDate(formData.value.dates.start.date, 'yyyy-mm-dd'),
        //     dateB: formatDate(formData.value.dates.end.date, 'yyyy-mm-dd'),
        //     _dateA: formatDate(formData.value.dates.start.date, 'day, dd/mm/yy'),
        //     _dateB: formatDate(formData.value.dates.end.date, 'day, dd/mm/yy'),
        //     timeA: formData.value.dates.start.time,
        //     timeB: formData.value.dates.end.time,
        //     adults: formData.value.guests?.guests?.adult.value,
        //     children: formData.value.guests?.guests?.children.value,
        //     infants: formData.value.guests?.guests?.infants.value,
        //     passengers: 1,
        //     promo: '',
        //     agent: 'Lounges',
        //     params: [],
        //     terminal: '',
        //     quoteID: '',
        // };

        // emit('submitSearch', query);
    }
};
</script>

<style lang="postcss" scoped>
button[type='submit']:disabled {
    @apply cursor-not-allowed opacity-70;
}
form.not-hydrated {
    pointer-events: none;
}
</style>
